/**
 * Controle do cache da tela do orquestrador (05/10).
 *
 * Pedido do dono: *"precisamos ir validando algumas coisas praticamente em tempo
 * real, portanto, tire o TTL do cache no backend de 5 minutos, e tb tire o TTL
 * de cache pro usuario, (mantenha o cache so tire o prazo de atualização)"*.
 *
 * MÓDULO PRÓPRIO, fora de `api.ts`, de propósito: estas são funções puras, e
 * `api.ts` importa o axios — que o Jest do CRA não transpila (ESM em
 * `node_modules`). Mantê-las aqui deixa o comportamento testável sem mock.
 */

/**
 * Estado do "Atualizar agora", em módulo e não em `window`.
 *
 * Módulo porque o pedido é de UMA rodada: quem liga é o botão, quem desliga é o
 * `finally` dele, e ninguém mais precisa enxergar isso. Em `window` ficaria
 * acessível de qualquer lugar e, pior, sobreviveria a um erro não tratado —
 * deixando a tela em modo "sem cache" sem ninguém perceber.
 */
let forcandoSemCache = false;

/** Liga/desliga o modo "ignorar o cache do servidor" na próxima rodada. */
export const definirForcarSemCache = (valor: boolean): void => {
  forcandoSemCache = valor;
};

/** Só para teste e diagnóstico: o estado atual do modo. */
export const estaForcandoSemCache = (): boolean => forcandoSemCache;

/**
 * `staleTime` das queries do orquestrador.
 *
 * ZERO: antes eram 300000 (5 min), espelhando o TTL do BFF — e o efeito era que
 * uma correção JÁ no ar só aparecia no ciclo seguinte, dando a impressão de que
 * o deploy não tinha pegado.
 *
 * Zero NÃO desliga o cache do React Query: os dados seguem guardados (voltar
 * para a aba não pisca tela em branco), só deixam de ser considerados "frescos"
 * — toda montagem/foco pergunta ao servidor. A resposta é barata porque o BFF
 * tem o seu próprio cache curto (`_TTL_CACHE`, 5s), que absorve a rajada.
 */
export const STALE_TEMPO_REAL = 0;

/**
 * Acrescenta `nocache: 1` enquanto o botão "Atualizar agora" estiver em curso.
 *
 * Fora desse intervalo devolve os params intactos e a chamada usa o cache
 * normalmente — o clique força UMA rodada, não todas as seguintes.
 *
 * No backend, `?nocache=1` pula a LEITURA mas mantém a GRAVAÇÃO: quem clica paga
 * a agregação e o resultado fresco é republicado para todos. O parâmetro NÃO
 * entra na chave do cache, de propósito (ver `_CONTROLE_CACHE` no BFF).
 */
export const comNoCache = <T extends object>(params: T): T | (T & { nocache: 1 }) =>
  forcandoSemCache ? { ...params, nocache: 1 as const } : params;

/**
 * "14:32:05" — de quando são os dados (`calculado_em` do BFF, 24/10). O BFF
 * guarda as respostas do monitoramento e as renova por trás, então o dado pode
 * ter alguns segundos: a tela diz de quando é. Sem carimbo (BFF antigo) → null.
 */
export const horarioDosDados = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/** Só as queries DESTA tela são refeitas pelo botão (todas começam com `orq`). */
export const ehQueryDoOrquestrador = (queryKey: readonly unknown[] | undefined): boolean =>
  String(queryKey?.[0] ?? '').startsWith('orq');
